import { useEffect, useState, useCallback, useRef } from "react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { chunkedUpload, MAX_UPLOAD_MB } from "../lib/chunkedUpload";
import { useAuth } from "../lib/auth";
import { Paperclip, Upload, Loader2, X, FileText, Download } from "lucide-react";
import { toast } from "sonner";

// Reusable attachments uploader backed by object storage.
// Usage: <Attachments context="bid" refId={bid.id} />
export function Attachments({ context, refId, title = "Attachments" }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/files?context=${encodeURIComponent(context)}&ref_id=${encodeURIComponent(refId)}`)
      .then((r) => setFiles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [context, refId]);
  useEffect(() => { load(); }, [load]);

  async function uploadFiles(fileList) {
    const arr = Array.from(fileList || []).filter((f) => {
      if (f.size > MAX_UPLOAD_MB * 1024 * 1024) { toast.error(`${f.name} is larger than ${MAX_UPLOAD_MB} MB and was skipped`); return false; }
      return true;
    });
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const f of arr) {
        await chunkedUpload(f, { kind: "files", context, ref_id: refId });
      }
      toast.success(arr.length > 1 ? `${arr.length} files uploaded` : "File uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  }

  async function remove(f) {
    if (!confirm(`Delete ${f.original_filename}?`)) return;
    try { await api.delete(`/files/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete"); }
  }

  const canDelete = (f) => f.uploaded_by === user?.id || user?.role === "admin";

  return (
    <div className="mt-4 border-t border-[#E5E3DB] pt-3" data-testid={`attachments-${context}-${refId}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wide text-[#68665E] font-semibold flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> {title} {files.length > 0 && `(${files.length})`}</span>
        <button onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-ghost text-xs" data-testid={`attachment-upload-btn-${refId}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" data-testid={`attachment-input-${refId}`}
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
        className={`rounded border border-dashed p-3 text-center text-xs transition-colors ${dragOver ? "border-[#C84B31] bg-[#FBEAE7]" : "border-[#D8D6CE]"}`}
        data-testid={`attachment-dropzone-${refId}`}
      >
        {loading ? <span className="text-[#A09E96]">Loading…</span>
          : files.length === 0 ? <span className="text-[#A09E96]">Drag &amp; drop files here, or click Upload</span>
          : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {files.map((f) => (
              <div key={f.id} className="relative group border border-[#E5E3DB] rounded bg-white overflow-hidden" data-testid={`attachment-item-${f.id}`}>
                <a href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer" className="block">
                  {f.is_image ? (
                    <img src={`${API}/files/${f.id}/download`} alt={f.original_filename} className="w-full h-20 object-cover" />
                  ) : (
                    <div className="w-full h-20 flex items-center justify-center bg-[#F5F4F0]"><FileText className="w-7 h-7 text-[#C84B31]" /></div>
                  )}
                  <div className="px-1.5 py-1 text-[10px] text-[#4A4842] truncate text-left flex items-center gap-1"><Download className="w-3 h-3 shrink-0" />{f.original_filename}</div>
                </a>
                {canDelete(f) && (
                  <button onClick={() => remove(f)} title="Delete" data-testid={`attachment-delete-${f.id}`}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
